import { IShippingAddressDTO } from 'pages/CheckoutPage/types';
import { queryClient } from 'index';
import i18n from 'providers/i18n/i18n';
import { RoutesConfig } from 'navigation/routes';
import { useMutation, useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { getToastErrorMessage, LogyxError } from 'services/api/errors';
import { ReactMutationKeys } from 'services/api/reactMutationKeys';
import { ReactQueryKeys } from 'services/api/reactQueryKeys';
import {
  checkIfCartExists,
  createNewCart,
  getShoppingCart,
  postCartOrder,
} from 'services/ShoppingCartService';
import { AppMode } from 'store/Common/reducers/common';
import { setActiveTab } from 'store/Order/actions/order';
import { setShoppingCartId } from 'store/ShoppingCart/actions/shoppingCart';
import { OrderTabsEnum } from 'types/Order.types';
import { useCartOrderAnswers } from 'utils/hooks/useCartOrderAnswers';
import { useOrderRefusal } from 'utils/hooks/useOrderRefusal';

export const useCheckIfCartExists = (companyId: number, appMode: AppMode) =>
  useQuery({
    queryKey: [ReactQueryKeys.CHECK_IF_CART_EXISTS, companyId, appMode],
    queryFn: () => {
      return checkIfCartExists({ companyId, appMode });
    },
    enabled: !!companyId,
  });

// "Create new cart" replaces the user's cart for this buyer and seller. Logyx core
// refuses while an order of that cart is being sent or checked, may already be at
// the supplier, or failed and can be sent again (docs/BP-1219-ordering.md): Forms
// says why and opens that order.
export const useCreateNewCart = (appMode: AppMode) => {
  const dispatch = useDispatch();
  const openRefusedOrder = useOrderRefusal(
    RoutesConfig.SupplierSingleOrder.fullPath,
    'newCart'
  );
  return useMutation(
    (params: { companyId: number }) =>
      createNewCart({ companyId: params.companyId, appMode }),
    {
      onSuccess: (data) => {
        if (!data?.cart_id) {
          toast.error(
            i18n.t(
              'Something went wrong while creating the shopping cart. Please try again.'
            )
          );
          return;
        }
        dispatch(setShoppingCartId(data.cart_id));
        dispatch(setActiveTab(OrderTabsEnum.Products));
      },
      onError: (error: LogyxError) => {
        if (openRefusedOrder(error)) {
          return;
        }
        toast.error(getToastErrorMessage(error));
      },
      mutationKey: ReactMutationKeys.CREATE_NEW_CART,
    }
  );
};

export interface IPostCartOrderDTO {
  cart_id: string;
  order_reference: string;
  requested_delivery_date: string | null;
  shipping_address_code?: string | null;
  shipping_address_data?: IShippingAddressDTO | null;
  is_pickup?: boolean;
}

// onNoAnswer: the answer was lost (504, a network error), and the order may have
// gone through (docs/BP-1219-ordering.md). A 404 reads the cart back, to tell a
// cart that was ordered from one that could not be.
export const usePostCartOrder = (appMode: AppMode, onNoAnswer: () => void) => {
  const { onSuccess, onError } = useCartOrderAnswers(
    RoutesConfig.SupplierSingleOrder.fullPath,
    RoutesConfig.SupplierOrders.fullPath,
    onNoAnswer,
    (cartId) => getShoppingCart(appMode, cartId)
  );
  return useMutation(
    (postCardOrderDTO: IPostCartOrderDTO) => {
      return postCartOrder(postCardOrderDTO);
    },
    {
      onSuccess,
      onError,
      onSettled: () => {
        queryClient.removeQueries(ReactQueryKeys.GET_SHOPPING_CART);
        queryClient.removeQueries(ReactQueryKeys.CHECK_IF_CART_EXISTS);
      },
      mutationKey: ReactMutationKeys.POST_CARD_ORDER,
    }
  );
};
